// Copyright 2021 Pangea Cyber Corporation
// Author: Pangea Cyber Corporation
import reduce from "lodash/reduce";
export var CONTENT_OPTIONS = {
    mlp: {
        title: "Security Services for Developers",
        subhead: "Get the APIs you need to deliver a secure customer experience, all in one place!",
        bullets: [
            {
                title: "Secure Apps. Faster, Stronger, Smarter",
                body: "Take the smart path and integrate Pangea into your application. You'll accelerate compliance, get into production faster, and deliver a more secure customer experience.",
            },
            {
                title: "Complete",
                body: "From easy pay-as-you-go pricing, to SDKs and Sample Applications, you have everything you need to embed security into your application.",
            },
            {
                title: "Simple",
                body: "More time building your app and less time building security features, so you get to production faster.",
            },
        ],
    },
    access: {
        title: "Control Access with Pangea’s AuthZ API Solution",
        subhead: "Easy and scalable permissions management for your apps using frameworks like RBAC, ABAC, and ReBAC.",
        bullets: [
            {
                title: "Granular Permissions",
                body: "Assign precise access policies based on roles, attributes, or relationships.",
            },
            {
                title: "Flexible and Scalable",
                body: "Use RBAC, ABAC, and ReBAC to match your security model and scale easily.",
            },
            {
                title: "Enterprise Security",
                body: "Simplify access while meeting compliance and security standards.",
            },
        ],
        quote: {
            text: "Hand-rolling something like this would just take more time and developer time is expensive, so we leveraged Pangea and liked the way it turned out.",
            attr: "Senior Software Engineer, Samuel Vidovic, Reach Security",
        },
    },
    authz: {
        title: "Centralized Access Control with Pangea AuthZ",
        subhead: "Ensure that the right users have access to the right resources with fine-grained access control.",
        bullets: [
            {
                title: "Flexible Policies",
                body: "Define access controls with granular permissions and roles.",
            },
            {
                title: "Scalable",
                body: "Start with RBAC, and scale to ReBAC and ABAC as you grow.",
            },
            {
                title: "Few lines of code",
                body: "Homegrown authorization frameworks break and need 1000s of lines of code. Ours only needs a few.",
                docsLink: "/docs/authz/",
            },
        ],
        quote: {
            text: "Rather than building all of that ourselves, it was much easier to integrate Pangea’s APIs and get the security features with virtually no development effort on our side.",
            attr: "Garrett Hamilton, Reach Security CEO",
        },
    },
    sso: {
        title: "Simplify User Access with Pangea's SSO",
        subhead: "Single Sign-On for your app with SAML, Social Auth, or Magic Links – backed by threat intelligence.",
        bullets: [
            {
                title: "Effortless Integration",
                body: "Set up SSO in minutes with SAML or OAuth + detailed.",
                docsLink: "/docs/authn/single-sign-on",
            },
            {
                title: "Enhanced Security",
                body: "Get adaptive threat intelligence and advanced authentication context.",
            },
            {
                title: "Enterprise-Ready",
                body: "Ensure compliance with leading standards like HIPAA, GDPR, and SOC 2.",
            },
        ],
        quote: {
            text: "All of their API calls were straightforward and simple. With an authentication token and some light configuration, we were ready to deploy immediately.",
            attr: "Sharon Brizinov, Co-founder ScanMySMS",
        },
    },
    oauth: {
        title: "Simplify User Authentication with Social Logins",
        subhead: "Let users login with Google, GitHub, or LinkedIn. Simple and secure auth with OpenID Connect protocols.",
        bullets: [
            {
                title: "Quick Setup",
                body: "Enable OAuth and Social Logins with minimal configuration.",
                docsLink: "/docs/authn/general#social-oauthopenid-connect",
            },
            {
                title: "Improved User Experience",
                body: "Reduce friction by letting users log in with accounts they already trust.",
            },
            {
                title: "Secure and Compliant",
                body: "Ensure compliance with leading security standards and privacy regulations.",
            },
        ],
        quote: {
            text: "Hand-rolling something like this would just take more time and developer time is expensive, so we leveraged Pangea and liked the way it turned out.",
            attr: "Senior Software Engineer, Samuel Vidovic, Reach Security",
        },
    },
    saml: {
        title: "Simplify Auth with Pangea’s SAML Support",
        subhead: "Implement SSO with SAML easily. Protect your app and simplify user access with our API solution.",
        bullets: [
            {
                title: "Easy Integration",
                body: "Get started in minutes with our step-by-step guide.",
                docsLink: "/docs/authn/single-sign-on",
            },
            {
                title: "Enterprise-Grade Security",
                body: "Fully compliant with HIPAA, GDPR, and SOC 2.",
            },
            {
                title: "Streamline Access",
                body: "Allow users to log in once and access all applications securely with SAML.",
            },
        ],
        quote: {
            text: "All of their API calls were straightforward. With an authentication token and some light configuration we were ready to deploy immediately.",
            attr: "Sharon Brizinov, Co-founder ScanMySMS",
        },
    },
    log: {
        title: "Ensure App Compliance and Security with Pangea Secure Audit Logging",
        subhead: "Ensure log integrity, data protection, and compliance with Pangea’s easy to integrate audit logging API.",
        bullets: [
            {
                title: "Quick Setup",
                body: "Implement our secure audit logging API in minutes with detailed.",
                docsLink: "/docs/audit/",
            },
            {
                title: "Tamper-Proof Logs",
                body: "Ensure log integrity with cryptographic hashing and tamper-evident features.",
            },
            {
                title: "Compliance-Ready",
                body: "Meet standards like HIPAA, GDPR, and SOC 2 effortlessly with built-in security features.",
            },
        ],
        quote: {
            text: "It was much easier to integrate Pangea’s logging APIs and get the security features like log signing and verification with virtually no development effort on our side. It was a no-brainer.",
            attr: "Garrett Hamilton, Reach Security CEO",
        },
    },
    totp: {
        title: "Strengthen Security with TOTP Authentication",
        subhead: "Add extra security with Time-based One-Time Passwords (TOTP). Protect users with Pangea’s auth.",
        bullets: [
            {
                title: "Quick Setup",
                body: "Get TOTP up and running with our easy-to-use API.",
                docsLink: "/docs/authn/",
            },
            {
                title: "Increased Security",
                body: "Stop bots and bad actors from accessing your app with time-based one-time codes.",
            },
            {
                title: "Custom Branding",
                body: "Use fully-brandable hosted pages for a consistent user experience.",
            },
        ],
        quote: {
            text: "All of their API calls were straightforward. With an authentication token and some light configuration we were ready to deploy immediately.",
            attr: "Sharon Brizinov, Co-founder ScanMySMS",
        },
    },
    mfa: {
        title: "Secure Your App and Users with Multi-Factor Authentication",
        subhead: "Protect users and apps with MFA in 2 minutes. Get customizable hosted login pages and API-native flows.",
        bullets: [
            {
                title: "Quick Setup",
                body: "Deploy end-to-end MFA and secure authentication flows in minutes.",
                docsLink: "/docs/authn/",
            },
            {
                title: "Advanced Security",
                body: "Stop account takeovers with customizable MFA and threat intelligence.",
            },
            {
                title: "Custom Branding",
                body: "Use fully-brandable hosted pages for a seamless user experience.",
            },
        ],
        quote: {
            text: "It was much easier to integrate Pangea’s APIs and get the security features with virtually no development effort on our side. It was a no-brainer.",
            attr: "Garrett Hamilton, Reach Security CEO",
        },
    },
    passkeys: {
        title: "Secure & Passwordless Authentication with Pangea’s Passkeys",
        subhead: "Say goodbye to passwords with Passkeys. Implement secure, FIDO2-based logins with public-key cryptography via our API solution.",
        bullets: [
            {
                title: "Password-Free Security",
                body: "Leverage Passkeys in your app for more user-friendly login experiences without passwords or MFA.",
            },
            {
                title: "Advanced Cryptography",
                body: "Protect user accounts using industry-leading public/private key authentication.",
            },
            {
                title: "Fast and Easy Integration",
                body: "Add Passkeys to your app quickly with Pangea’s API-driven solution.",
                docsLink: "/docs/authn/single-sign-on#passkeys",
            },
        ],
        quote: {
            text: "The integration with Pangea was very straightforward. It was quick and easy to work with the APIs and even better to have native language support from the SDK.",
            attr: "Noah Mirzakhanian, Engineering, Codex",
        },
    },
    scim: {
        title: "Automate User Management with SCIM",
        subhead: "Manage user identities and provisioning with SCIM. Streamline onboarding and stay security and compliant.",
        bullets: [
            {
                title: "Seamless Integration",
                body: "Implement SCIM in minutes with fully-brandable hosted pages across all systems.",
                docsLink: "/docs/authn/",
            },
            {
                title: "Reduced Admin Overhead",
                body: "Automatically manage users and roles with minimal effort.",
            },
            {
                title: "Enterprise Compliance",
                body: "Align with leading security and privacy standards.",
            },
        ],
        quote: {
            text: "All of their API calls were straightforward and simple. With an authentication token and some light configuration we were ready to deploy immediately.",
            attr: "Sharon Brizinov, Co-founder ScanMySMS",
        },
    },
    webauthn: {
        title: "Simplify Authentication with WebAuthN and Pangea",
        subhead: "Protect your users with secure, standard website authentication. Implement in minutes with adaptive threat intelligence.",
        bullets: [
            {
                title: "Quick Setup",
                body: "Secure your website with API-driven authentication flows in minutes.",
                docsLink: "/docs/authn/",
            },
            {
                title: "Enhanced Security",
                body: "Block account takeovers using IP, user, and domain intelligence.",
            },
            {
                title: "Custom Branding",
                body: "Deploy fully-brandable login pages for a seamless user experience.",
            },
        ],
        quote: {
            text: "The integration with Pangea was very straightforward. It was quick and easy to work with the APIs and even better to have native language support from the SDK.",
            attr: "Noah Mirzakhanian, Engineering, Codex",
        },
    },
    permission: {
        title: "Simplify Permission Management Across Your Apps",
        subhead: "Manage who can do what in your app with Pangea's customizable permission management system.",
        bullets: [
            {
                title: "Granular Permissions",
                body: "Define precise permissions with role, relationship, or attribute-based controls.",
            },
            {
                title: "Unified Policy Management",
                body: "Centralize permission management to avoid sprawl across apps.",
            },
            {
                title: "Fast Integration",
                body: "Use SDKs and APIs to integrate permissions into any app in minutes.",
                docsLink: "/docs/authz/",
            },
        ],
        quote: {
            text: "The integration with Pangea was very straightforward. It was quick and easy to work with the APIs and even better to have native language support from the SDK.",
            attr: "Noah Mirzakhanian, Engineering, Codex",
        },
    },
};
export var getContentKeys = function () {
    return reduce(Object.keys(CONTENT_OPTIONS), function (result, key) {
        result[key] = true;
        return result;
    }, {});
};
